import type { IPublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import {
  StorageKeys,
  clearInteractionStatus,
  consumeLoginHint,
  readStorage,
  removeStorage,
  saveReturnTo,
  writeStorage,
} from "./browserStorage";

export type AuthView = "signIn" | "signUp" | "resetPassword";

const AUTH_ROUTES = new Set(["/login", "/signup", "/reset-password", "/logged-out"]);
const LOOP_WINDOW_MS = 2 * 60 * 1000;
const MAX_STARTS_PER_WINDOW = 3;

/** True while the browser is navigating to the hosted page, so a remount cannot start a second one. */
let redirectInFlight = false;
const PLACEHOLDER_ORIGIN = "https://return-to.invalid";

export class RedirectLoopError extends Error {
  constructor() {
    super("Hosted sign-in was started repeatedly without completing.");
    this.name = "RedirectLoopError";
  }
}

/**
 * A same-origin path to return to after sign-in. Never another origin, a protocol-relative URL or
 * an auth route; the ?affiliate= parameter is dropped because it only matters on the sign-in page.
 */
export function safeReturnTo(
  candidate: string = `${window.location.pathname}${window.location.search}${window.location.hash}`
): string {
  if (!candidate.startsWith("/") || candidate.startsWith("//") || candidate.includes("\\")) {
    return "/";
  }

  let url: URL;
  try {
    url = new URL(candidate, PLACEHOLDER_ORIGIN);
  } catch {
    return "/";
  }

  if (url.origin !== PLACEHOLDER_ORIGIN || AUTH_ROUTES.has(url.pathname.toLowerCase())) {
    return "/";
  }

  url.searchParams.delete("affiliate");
  const search = url.searchParams.toString();
  return `${url.pathname}${search ? `?${search}` : ""}${url.hash}`;
}

/** Returns false when hosted sign-in has already been started too often in a short window. */
export function registerRedirectStart(now: number = Date.now()): boolean {
  const recent = (readStorage("session", StorageKeys.redirectStarts) ?? "")
    .split(",")
    .map(Number)
    .filter((startedAt) => startedAt > 0 && now - startedAt < LOOP_WINDOW_MS);

  if (recent.length >= MAX_STARTS_PER_WINDOW) {
    return false;
  }

  writeStorage("session", StorageKeys.redirectStarts, [...recent, now].join(","));
  return true;
}

export function resetRedirectLoopGuard(): void {
  redirectInFlight = false;
  removeStorage("session", StorageKeys.redirectStarts);
}

/**
 * Browser-delegated sign-in on the Microsoft-hosted External ID page, which shows the tenant's
 * default branding. Same client ID, authority and scope as native sign-in.
 */
export async function startHostedSignIn(
  pca: IPublicClientApplication,
  options: { view?: AuthView; loginHint?: string; returnTo?: string } = {}
): Promise<void> {
  if (redirectInFlight) {
    // Already navigating; calling loginRedirect again throws interaction_in_progress.
    return;
  }

  if (!registerRedirectStart()) {
    throw new RedirectLoopError();
  }

  saveReturnTo(options.returnTo ?? safeReturnTo());
  redirectInFlight = true;

  try {
    await pca.loginRedirect({
      ...loginRequest,
      loginHint: options.loginHint ?? consumeLoginHint() ?? undefined,
      prompt: options.view === "signUp" ? "create" : undefined,
    });
  } catch (error) {
    redirectInFlight = false;
    if ((error as { errorCode?: string } | null)?.errorCode === "interaction_in_progress") {
      // Left over from an interrupted redirect: clear it so the next attempt can navigate.
      clearInteractionStatus();
    }
    throw error;
  }
}
